import { useCallback, useMemo, useState } from 'react'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { Panel } from '@components/Panel/Panel'
import { HoverableReviewStars } from '@components/ReviewStars/ReviewStars'
import { baseScanUrl } from '@components/Web3/utils'
import { useQueryClient } from '@tanstack/react-query'
import { Box, Button, Paragraph, Stack, TextArea } from '@ui'
import { SECOND_MS } from 'data/constants'
import { useEnvironment } from 'hooks/useEnvironment'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { useAgentPromotionStatus, useGiveBotFeedbackTransaction } from 'use-towns-client'
import { isAddress, keccak256, toBytes } from 'viem'

interface Props {
    botId: string
}

export const BotReviewPanel = (props: Props) => {
    const botId = isAddress(props.botId) ? props.botId : undefined
    const { data: promotionStatus } = useAgentPromotionStatus(botId)
    const agentId = promotionStatus?.agentId
    const isPromoted = promotionStatus?.isPromoted ?? false
    const { closePanel } = usePanelActions()
    const queryClient = useQueryClient()

    const derivedAgentId = useMemo(
        () =>
            isPromoted && promotionStatus?.agentId !== undefined
                ? (BigInt(promotionStatus.agentId.toString()) as bigint)
                : undefined,
        [isPromoted, promotionStatus?.agentId],
    )
    const effectiveAgentId = agentId ?? derivedAgentId
    const canReview = isPromoted && effectiveAgentId !== undefined

    const handleSubmitted = useCallback(() => {
        // Invalidate bot reviews query so the list refreshes
        if (botId) {
            void queryClient.invalidateQueries({ queryKey: ['bot-reviews', botId] })
        }
        // Close panel after a short delay to allow user to see the success toast
        setTimeout(() => {
            closePanel()
        }, 500)
    }, [closePanel, queryClient, botId])

    return (
        <Panel label="Write a Review">
            <Stack gap>
                {canReview ? (
                    <BotReviewForm
                        agentId={effectiveAgentId as bigint}
                        onSubmitted={handleSubmitted}
                    />
                ) : (
                    <Paragraph color="gray2">
                        Bot must be promoted before it can receive reviews.
                    </Paragraph>
                )}
            </Stack>
        </Panel>
    )
}

const MIN_REVIEW_LENGTH = 10
const MAX_REVIEW_LENGTH = 4000
const TOAST_ID = 'bot-review-submission'

interface BotReviewFormProps {
    agentId: bigint
    existingReview?: {
        rating: number
        text: string
    }
    onSubmitted?: () => void
}

const BotReviewForm = ({ agentId, existingReview, onSubmitted }: BotReviewFormProps) => {
    const [rating, setRating] = useState(existingReview?.rating || 0)
    const [reviewText, setReviewText] = useState(existingReview?.text || '')

    const { giveBotFeedback, isLoading } = useGiveBotFeedbackTransaction()
    const { baseChain } = useEnvironment()

    const isEditing = !!existingReview

    const canSubmit = useMemo(() => {
        return (
            rating > 0 &&
            reviewText.length >= MIN_REVIEW_LENGTH &&
            reviewText.length <= MAX_REVIEW_LENGTH &&
            !isLoading
        )
    }, [rating, reviewText, isLoading])

    const handleSubmit = useCallback(
        async (getSigner: GetSigner) => {
            if (!canSubmit) {
                return
            }

            // Show pending toast
            popupToast(
                ({ toast }) => (
                    <StandardToast.Pending toast={toast} message="Submitting review..." />
                ),
                {
                    id: TOAST_ID,
                    duration: Number.POSITIVE_INFINITY,
                },
            )

            try {
                const signer = await getSigner()

                if (!signer) {
                    popupToast(
                        ({ toast }) => (
                            <StandardToast.Error message="Wallet not available" toast={toast} />
                        ),
                        {
                            id: TOAST_ID,
                            duration: SECOND_MS * 4,
                        },
                    )
                    return
                }

                // Create feedback URI (for now, just store as JSON string)
                // TODO: Upload to IPFS in the future
                const feedbackData = {
                    text: reviewText,
                    timestamp: Date.now(),
                }
                const feedbackUri = `data:application/json,${encodeURIComponent(JSON.stringify(feedbackData))}`

                // Create feedback hash
                const feedbackHash = keccak256(toBytes(JSON.stringify(feedbackData)))

                const result = await giveBotFeedback(
                    {
                        agentId,
                        score: rating * 20,
                        tag1: '',
                        tag2: '',
                        feedbackUri,
                        feedbackHash,
                        signer,
                    },
                    {
                        onSuccess: () => {
                            onSubmitted?.()
                        },
                        onError: (error) => {
                            const errorMessage = 'Failed to submit review'
                            const subMessage = 'Please try again.'

                            console.error(error)

                            popupToast(
                                ({ toast }) => (
                                    <StandardToast.Error
                                        message={errorMessage}
                                        subMessage={subMessage}
                                        toast={toast}
                                    />
                                ),
                                {
                                    id: TOAST_ID,
                                    duration: SECOND_MS * 5,
                                },
                            )
                        },
                    },
                )

                // Show success toast with transaction link if available
                if (result?.transaction) {
                    const message = isEditing
                        ? 'Review updated successfully'
                        : 'Review posted successfully'

                    let transactionHash: string | undefined

                    const tx = result.transaction
                    if ('getUserOperationReceipt' in tx) {
                        // For user operations, wait for the receipt to get the final transaction hash
                        const receipt = await tx.getUserOperationReceipt()
                        transactionHash = receipt?.receipt?.transactionHash
                    } else {
                        transactionHash = tx.hash
                    }

                    if (transactionHash) {
                        const transactionUrl = `${baseScanUrl(baseChain.id)}/tx/${transactionHash}`
                        popupToast(
                            ({ toast: toastInstance }) => (
                                <StandardToast.Success
                                    message={message}
                                    toast={toastInstance}
                                    cta="View Transaction"
                                    onCtaClick={({ dismissToast }) => {
                                        window.open(transactionUrl, '_blank', 'noopener,noreferrer')
                                        dismissToast()
                                    }}
                                />
                            ),
                            {
                                id: TOAST_ID,
                                duration: SECOND_MS * 5,
                            },
                        )
                    } else {
                        // If we couldn't get the transaction hash, show success without the link
                        popupToast(
                            ({ toast: toastInstance }) => (
                                <StandardToast.Success message={message} toast={toastInstance} />
                            ),
                            {
                                id: TOAST_ID,
                                duration: SECOND_MS * 5,
                            },
                        )
                    }
                }
            } catch (error) {
                console.error('Failed to submit review:', error)
                popupToast(
                    ({ toast }) => (
                        <StandardToast.Error
                            message="Failed to submit review"
                            subMessage="Please try again."
                            toast={toast}
                        />
                    ),
                    {
                        id: TOAST_ID,
                        duration: SECOND_MS * 5,
                    },
                )
            }
        },
        [
            canSubmit,
            reviewText,
            rating,
            agentId,
            giveBotFeedback,
            onSubmitted,
            isEditing,
            baseChain.id,
        ],
    )

    return (
        <Box gap>
            <Stack gap>
                <Stack centerContent height="x8">
                    <HoverableReviewStars initialRating={rating} size={32} onChange={setRating} />
                </Stack>

                <Stack gap="paragraph">
                    <Paragraph>Share details about the experience with this bot.</Paragraph>
                    <TextArea
                        paddingY="md"
                        background="level2"
                        value={reviewText}
                        placeholder="Write a review..."
                        rows={9}
                        minHeight="x12"
                        onChange={(e) => setReviewText(e.target.value)}
                    />
                </Stack>

                <WalletReady>
                    {({ getSigner }) => (
                        <Button
                            disabled={!canSubmit}
                            tone={canSubmit ? 'cta1' : 'level2'}
                            onClick={() => handleSubmit(getSigner)}
                        >
                            {isLoading
                                ? 'Submitting...'
                                : isEditing
                                  ? 'Update Review'
                                  : 'Submit Review'}
                        </Button>
                    )}
                </WalletReady>
            </Stack>
        </Box>
    )
}
